import React, { ChangeEvent, ClipboardEvent, KeyboardEvent, useEffect, useState } from 'react';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Loader from '../../../components/Common/Loader';
import BackButton from '../../../components/Common/BackButton';
import CleverImportButton from './CleverImportButton';
import CleverClassroomSelector from './CleverClassroomSelector';
import CleverStudentSelector from './CleverStudentSelector';
import { CleverClassroom, importCleverStudents } from '../services/CleverStudentImport.service';
import GoogleClassroomCourseSelector from './GoogleClassroomCourseSelector';
import GoogleClassroomStudentSelector from './GoogleClassroomStudentSelector';
import {
  checkGoogleClassroomIntegration,
  GoogleClassroomCourse,
  importGoogleClassroomStudents,
} from '../services/GoogleClassroomStudentImport.service';
import { checkCleverIntegration } from '../../../services/cleverAuth.service';
import { CleverAuthTabService } from '../../../services/cleverAuthTab.service';
import { GoogleClassroomAuthTabService } from '../services/GoogleClassroomAuthTab.service';
import { Toast } from '../../../components/Common/Toast';
import GoogleClassroomImportButton from './GoogleClassroomImportButton';

interface ClassroomAddNewItemsProps {
  close?: () => void;
  back?: () => void;
  submit: (items: string[]) => void;
  skip?: () => void;
  loader?: boolean;
  isOnboarding?: boolean;
  limitClassroomStudents?: number | null;
  limitClassroomStudentsFeedback?: () => void;
  inputAsButton?: boolean;
  isCentered?: boolean;
  isInline?: boolean;
  classroomId?: string;
  projectId?: string;
  onStudentsImported?: () => void;
  onImportingFlow?: (isImporting: boolean) => void;
}

enum CleverImportStep {
  INITIAL = 'initial',
  AUTH = 'auth',
  SELECT_CLASSROOM = 'select_classroom',
  SELECT_STUDENTS = 'select_students',
}

enum GoogleClassroomImportStep {
  INITIAL = 'initial',
  AUTH = 'auth',
  SELECT_COURSE = 'select_course',
  SELECT_STUDENTS = 'select_students',
}

const StudentsAddNewItems: React.FC<ClassroomAddNewItemsProps> = ({
  close,
  back,
  skip,
  submit,
  loader,
  isOnboarding,
  limitClassroomStudents,
  limitClassroomStudentsFeedback,
  inputAsButton,
  isCentered,
  isInline,
  classroomId,
  projectId,
  onStudentsImported,
  onImportingFlow,
}: ClassroomAddNewItemsProps) => {
  const [namesList, setNamesList] = useState<string[]>([]);
  const [currentName, setCurrentName] = useState('');
  const [limitationError, setLimitationError] = useState<boolean>(false);
  const [similarityError, setSimilarityError] = useState<boolean>(false);

  // Clever import state
  const [cleverStep, setCleverStep] = useState<CleverImportStep>(CleverImportStep.INITIAL);
  const [selectedCleverClassroom, setSelectedCleverClassroom] = useState<CleverClassroom | null>(null);
  const [cleverImportLoading, setCleverImportLoading] = useState(false);

  // Google Classroom import state
  const [googleClassroomStep, setGoogleClassroomStep] = useState<GoogleClassroomImportStep>(
    GoogleClassroomImportStep.INITIAL,
  );
  const [selectedGoogleClassroomCourse, setSelectedGoogleClassroomCourse] = useState<GoogleClassroomCourse | null>(
    null,
  );
  const [googleClassroomImportLoading, setGoogleClassroomImportLoading] = useState(false);

  useEffect(() => {
    onImportingFlow &&
      onImportingFlow(
        cleverStep !== CleverImportStep.INITIAL || googleClassroomStep !== GoogleClassroomImportStep.INITIAL,
      );
  }, [cleverStep, googleClassroomStep]);

  useEffect(() => {
    setLimitationError((prevState) => {
      const isExceedingLimit =
        !!namesList &&
        (!!limitClassroomStudents || limitClassroomStudents === 0) &&
        limitClassroomStudents < namesList?.length;
      if (isExceedingLimit && !prevState) {
        limitClassroomStudentsFeedback && limitClassroomStudentsFeedback();
      }
      return isExceedingLimit;
    });
  }, [limitClassroomStudents, namesList]);

  useEffect(() => {
    const uniqueNames = new Set(namesList.map((name) => name.toLowerCase().trim()));
    setSimilarityError(uniqueNames.size !== namesList.length);
  }, [namesList]);

  const processNameString = (nameString: string) => {
    const names = nameString
      .split(/[,\n]/)
      .map((name) => name.trim())
      .filter((name) => name.length > 0);

    names.forEach((name) => {
      setNamesList((prev) => [...prev, name]);
      setCurrentName('');
    });
  };

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData('text');
    processNameString(pastedText);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',' || e.key === ' ') {
      e.preventDefault();
      if (currentName) {
        processNameString(currentName);
      }
    }
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value.endsWith(',')) {
      const emailToAdd = value.slice(0, -1);
      if (emailToAdd) {
        processNameString(emailToAdd);
      }
      setCurrentName('');
    } else {
      setCurrentName(value);
    }
  };

  const handleBlur = () => {
    if (currentName.trim()) {
      processNameString(currentName);
    }
  };

  const removeEmail = (emailToRemove: string, index: number) => {
    setNamesList(namesList.filter((email: string, i: number) => !(email === emailToRemove && i === index)));
  };

  // Clever import handlers
  const handleCleverImportClick = async () => {
    setCleverImportLoading(true);

    try {
      // Check if we have integration
      const hasIntegration = await checkCleverIntegration();

      if (hasIntegration) {
        // If authenticated, proceed directly to classroom selection
        setCleverStep(CleverImportStep.SELECT_CLASSROOM);
      } else {
        // If not authenticated, show auth step
        setCleverStep(CleverImportStep.AUTH);
      }
    } catch (error) {
      console.error('Error checking Clever integration:', error);
      Toast('Failed to check Clever integration. Please try again.', true);
    } finally {
      setCleverImportLoading(false);
    }
  };

  // Google Classroom import handlers
  const handleGoogleClassroomImportClick = async () => {
    setGoogleClassroomImportLoading(true);

    try {
      // Check if we have integration
      const hasIntegration = await checkGoogleClassroomIntegration();

      if (hasIntegration) {
        // If authenticated, proceed directly to course selection
        setGoogleClassroomStep(GoogleClassroomImportStep.SELECT_COURSE);
      } else {
        // If not authenticated, show auth step
        setGoogleClassroomStep(GoogleClassroomImportStep.AUTH);
      }
    } catch (error) {
      console.error('Error checking Google Classroom integration:', error);
      Toast('Failed to check Google Classroom integration. Please try again.', true);
    } finally {
      setGoogleClassroomImportLoading(false);
    }
  };

  const handleClassroomSelect = (classroom: CleverClassroom) => {
    setSelectedCleverClassroom(classroom);
    setCleverStep(CleverImportStep.SELECT_STUDENTS);
  };

  const handleStudentsSelect = async (studentIds: string[]) => {
    if (!classroomId || !projectId || !selectedCleverClassroom) {
      return;
    }

    try {
      setCleverImportLoading(true);
      const response = await importCleverStudents(
        selectedCleverClassroom.id,
        classroomId,
        projectId,
        studentIds,
        !!isOnboarding,
      );

      if (response?.status === 200) {
        // Close the modal and refresh the student list
        close && close();
        onStudentsImported && onStudentsImported();
      }
    } catch (error) {
      console.error('Error importing students from Clever:', error);
    } finally {
      setCleverImportLoading(false);
    }
  };

  const handleGoogleClassroomCourseSelect = (course: GoogleClassroomCourse) => {
    setSelectedGoogleClassroomCourse(course);
    setGoogleClassroomStep(GoogleClassroomImportStep.SELECT_STUDENTS);
  };

  const handleGoogleClassroomStudentsSelect = async (studentIds: string[]) => {
    if (!classroomId || !projectId || !selectedGoogleClassroomCourse) {
      return;
    }

    try {
      setGoogleClassroomImportLoading(true);
      const response = await importGoogleClassroomStudents(
        selectedGoogleClassroomCourse.id,
        classroomId,
        projectId,
        studentIds,
        !!isOnboarding,
      );

      if (response?.status === 200) {
        // Close the modal and refresh the student list
        close && close();
        onStudentsImported && onStudentsImported();
      }
    } catch (error) {
      console.error('Error importing students from Google Classroom:', error);
    } finally {
      setGoogleClassroomImportLoading(false);
    }
  };

  const handleCleverAuth = async () => {
    setCleverImportLoading(true);

    try {
      const success = await CleverAuthTabService.authenticateWithTab();
      if (success) {
        // After successful auth, proceed directly to classroom selection
        setTimeout(() => setCleverStep(CleverImportStep.SELECT_CLASSROOM), 1000);
      }
    } catch (error) {
      console.error('Error during Clever authentication:', error);
      Toast('Authentication failed. Please try again.', true);
    } finally {
      setCleverImportLoading(false);
    }
  };

  const handleCleverBack = async () => {
    if (cleverStep === CleverImportStep.SELECT_STUDENTS) {
      setCleverStep(CleverImportStep.SELECT_CLASSROOM);
      setSelectedCleverClassroom(null);
    } else if (cleverStep === CleverImportStep.SELECT_CLASSROOM) {
      // When going back from classroom selection, check if user got here via auth step or direct access
      try {
        const hasIntegration = await checkCleverIntegration();
        if (hasIntegration) {
          // If authenticated, go back to initial (they came here directly)
          setCleverStep(CleverImportStep.INITIAL);
        } else {
          // If not authenticated, go back to auth step
          setCleverStep(CleverImportStep.AUTH);
        }
      } catch (error) {
        // If error checking, assume they came through auth step
        setCleverStep(CleverImportStep.AUTH);
      }
    } else if (cleverStep === CleverImportStep.AUTH) {
      setCleverStep(CleverImportStep.INITIAL);
    }
  };

  const handleCleverClose = () => {
    setCleverStep(CleverImportStep.INITIAL);
    setSelectedCleverClassroom(null);
    close && close();
  };

  const handleGoogleClassroomAuth = async () => {
    setGoogleClassroomImportLoading(true);

    try {
      await GoogleClassroomAuthTabService.authenticateWithTab();
      const hasIntegration = await checkGoogleClassroomIntegration();
      if (hasIntegration) {
        // After successful auth, proceed directly to course selection
        setTimeout(() => setGoogleClassroomStep(GoogleClassroomImportStep.SELECT_COURSE), 1000);
      }
    } catch (error) {
      console.error('Error during Google Classroom authentication:', error);
      Toast('Authentication failed. Please try again.', true);
    } finally {
      setGoogleClassroomImportLoading(false);
    }
  };

  const handleGoogleClassroomBack = async () => {
    if (googleClassroomStep === GoogleClassroomImportStep.SELECT_STUDENTS) {
      setGoogleClassroomStep(GoogleClassroomImportStep.SELECT_COURSE);
      setSelectedGoogleClassroomCourse(null);
    } else if (googleClassroomStep === GoogleClassroomImportStep.SELECT_COURSE) {
      // When going back from course selection, check if user got here via auth step or direct access
      try {
        const hasIntegration = await checkGoogleClassroomIntegration();
        if (hasIntegration) {
          // If authenticated, go back to initial (they came here directly)
          setGoogleClassroomStep(GoogleClassroomImportStep.INITIAL);
        } else {
          // If not authenticated, go back to auth step
          setGoogleClassroomStep(GoogleClassroomImportStep.AUTH);
        }
      } catch (error) {
        // If error checking, assume they came through auth step
        setGoogleClassroomStep(GoogleClassroomImportStep.AUTH);
      }
    } else if (googleClassroomStep === GoogleClassroomImportStep.AUTH) {
      setGoogleClassroomStep(GoogleClassroomImportStep.INITIAL);
    }
  };

  const handleGoogleClassroomClose = () => {
    setGoogleClassroomStep(GoogleClassroomImportStep.INITIAL);
    setSelectedGoogleClassroomCourse(null);
    close && close();
  };

  // Render different steps
  if (cleverStep === CleverImportStep.AUTH) {
    return (
      <div
        className={`flex flex-col text-black font-sofiaSans px-4 sm:px-0 ${isCentered ? 'items-center' : 'items-start'}`}
      >
        <h2 className="w-full text-2xl md:text-2xl mb-4 font-bold text-storyblue main-title-modal-text-left">
          Import from Clever
        </h2>
        <div className={`grid grid-cols-1 w-full md:gap-6 text-justify modal-description md:w-full mb-2 min-w-full`}>
          <div style={{ display: 'block' }}>
            <h3 className={`text-base pb-1 text-storyblackText font-sofiaSans font-medium w-screen max-w-full`}>
              Login to Clever to import students
            </h3>
          </div>
        </div>

        <div className="flex justify-start w-full mb-6">
          <button
            onClick={handleCleverAuth}
            disabled={cleverImportLoading}
            className={`w-full sm:w-auto border font-semibold py-2 px-6 rounded-lg transition-colors duration-200 flex items-center justify-center gap-2 bg-secondaryStoryLightGrayButtonBackground border-secondaryStoryLightGrayButtonBackground text-secondaryStoryGrayButtonText hover:shadow rounded-lg hover:bg-secondaryStoryLightGrayButtonBackgroundHover hover:border-secondaryStoryLightGrayButtonBackgroundHover ${
              cleverImportLoading ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            {cleverImportLoading ? (
              <div className="flex items-center gap-2 text-sm">
                <span>Loading...</span>
              </div>
            ) : (
              <div className="flex items-center gap-2 text-sm">
                <svg width="24" height="24" viewBox="5 0 75 75" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M70 20C65 15 58 10 50 10C33.43 10 20 23.43 20 40C20 56.57 33.43 70 50 70C58 70 65 65 70 60L60 50C56 54 52 55 50 55C41.72 55 35 48.28 35 40C35 31.72 41.72 25 50 25C52 25 56 26 60 30L70 20Z"
                    fill="#166dff"
                  />
                </svg>
                <span>Login with Clever</span>
              </div>
            )}
          </button>
        </div>

        <div className={`flex flex-col gap-4 w-full sm:flex-row justify-between sm:gap-2`}>
          <div>
            <BackButton onClick={handleCleverBack}></BackButton>
          </div>
          <button
            className={`px-6 py-2 h-9 bg-secondaryStoryAdditionalLightBlueText text-whiteText font-bold leading-none rounded-2xl hover:shadow-md transition-shadow flex items-center justify-center text-sm sm:text-base w-full sm:w-auto opacity-50 cursor-not-allowed`}
            disabled={true}
          >
            Continue
          </button>
        </div>
      </div>
    );
  }

  if (cleverStep === CleverImportStep.SELECT_CLASSROOM) {
    return (
      <CleverClassroomSelector
        onClassroomSelect={handleClassroomSelect}
        onBack={handleCleverBack}
        onClose={handleCleverClose}
      />
    );
  }

  if (cleverStep === CleverImportStep.SELECT_STUDENTS && selectedCleverClassroom) {
    return (
      <CleverStudentSelector
        classroom={selectedCleverClassroom}
        onStudentsSelect={handleStudentsSelect}
        onBack={handleCleverBack}
        onClose={handleCleverClose}
        cleverImportLoading={cleverImportLoading}
      />
    );
  }

  if (googleClassroomStep === GoogleClassroomImportStep.AUTH) {
    return (
      <div
        className={`flex flex-col text-black font-sofiaSans px-4 sm:px-0 ${isCentered ? 'items-center' : 'items-start'}`}
      >
        <h2 className="w-full text-2xl md:text-2xl mb-4 font-bold text-storyblue main-title-modal-text-left">
          Import from Google Classroom
        </h2>
        <div className={`grid grid-cols-1 w-full md:gap-6 text-justify modal-description md:w-full mb-2`}>
          <div style={{ display: 'block' }}>
            <h3 className={`text-sm pb-1 text-storyblackText font-sofiaSans font-medium w-screen max-w-full`}>
              Login to Google Classroom to import students
            </h3>
          </div>
        </div>

        <div className="flex justify-start w-full mb-6">
          <button
            onClick={handleGoogleClassroomAuth}
            disabled={googleClassroomImportLoading}
            className={`w-full sm:w-auto border font-semibold py-2 px-6 rounded-lg transition-colors duration-200 flex items-center justify-center gap-2 bg-secondaryStoryLightGrayButtonBackground border-secondaryStoryLightGrayButtonBackground text-secondaryStoryGrayButtonText hover:shadow rounded-lg hover:bg-secondaryStoryLightGrayButtonBackgroundHover hover:border-secondaryStoryLightGrayButtonBackgroundHover ${
              googleClassroomImportLoading ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            {googleClassroomImportLoading ? (
              <div className="flex items-center gap-2 text-sm">
                <span>Loading...</span>
              </div>
            ) : (
              <div className="flex items-center gap-2 text-sm">
                <img src="/google-icon.png" alt="google" className="w-[20px]" />
                <span>Login with Google</span>
              </div>
            )}
          </button>
        </div>

        <div className={`flex flex-col gap-4 w-full sm:flex-row justify-between sm:gap-2`}>
          <div>
            <BackButton onClick={handleGoogleClassroomBack}></BackButton>
          </div>

          <button
            className={`px-6 py-2 h-9 bg-secondaryStoryAdditionalLightBlueText text-whiteText font-bold leading-none rounded-2xl hover:shadow-md transition-shadow flex items-center justify-center text-sm sm:text-base w-full sm:w-auto opacity-50 cursor-not-allowed`}
            disabled={true}
          >
            Continue
          </button>
        </div>
      </div>
    );
  }

  if (googleClassroomStep === GoogleClassroomImportStep.SELECT_COURSE) {
    return (
      <GoogleClassroomCourseSelector
        onCourseSelect={handleGoogleClassroomCourseSelect}
        onBack={handleGoogleClassroomBack}
        onClose={handleGoogleClassroomClose}
      />
    );
  }

  if (googleClassroomStep === GoogleClassroomImportStep.SELECT_STUDENTS && selectedGoogleClassroomCourse) {
    return (
      <GoogleClassroomStudentSelector
        course={selectedGoogleClassroomCourse}
        onStudentsSelect={handleGoogleClassroomStudentsSelect}
        onBack={handleGoogleClassroomBack}
        onClose={handleGoogleClassroomClose}
        googleClassroomImportLoading={googleClassroomImportLoading}
      />
    );
  }

  // Initial step with manual input and Clever import
  return (
    <div
      className={`flex flex-col text-black font-sofiaSans px-4 sm:px-0 ${isCentered ? 'items-center' : 'items-start'}`}
    >
      <h2 className="text-left text-2xl md:text-2xl mb-4 font-bold text-storyblue main-title-modal-text">
        Add Students
      </h2>
      <div className={`grid grid-cols-1 w-full md:gap-6 text-justify modal-description md:w-full`}>
        <div style={{ display: 'block' }}>
          <h3 className={`text-sm  pb-1 text-storyblackText font-sofiaSans font-medium`}>
            Add students, each on a new line or import from <span className="font-bold">Clever</span> or{' '}
            <span className="font-bold">Google Classroom</span>. You don’t need to use their full name, as long as you
            can tell them apart. We don’t share this list with anyone.
          </h3>
        </div>
      </div>

      {/* Import Buttons */}
      <div className="mt-4 gap-2 sm:flex justify-between sm:w-full">
        <CleverImportButton onImportClick={handleCleverImportClick} />
        <GoogleClassroomImportButton onImportClick={handleGoogleClassroomImportClick} />
      </div>

      <div className="w-full flex items-center gap-1 pt-4 pb-2 text-secondaryStoryGrayText">
        <hr className="w-[50%] border-secondaryBorderLightGreyColor" />
        <p>Or</p>
        <hr className="w-[50%] border-secondaryBorderLightGreyColor" />
      </div>

      <label htmlFor="emails" className="block mb-2 font-bold text-black text-sm sm:text-base">
        Names
      </label>

      <div
        className={`w-full min-h-[84px] border ${similarityError || limitationError ? 'border-storyred' : 'border-gray-300'} rounded p-2 mb-4`}
      >
        <div className={`w-full flex flex-wrap gap-1 ${isInline ? 'max-w-[400px]' : 'max-w-full sm:max-w-[500px]'}`}>
          {namesList.map((email, index) => (
            <div
              key={index}
              className="flex items-center bg-gray-100 rounded-full px-2 sm:px-3 py-1 text-xs sm:text-sm"
            >
              <span className="truncate max-w-[120px] sm:max-w-none">{email}</span>
              <button
                onClick={() => removeEmail(email, index)}
                className="ml-1 sm:ml-2 text-gray-500 hover:text-gray-700 flex-shrink-0"
                type="button"
              >
                <FontAwesomeIcon icon={faTimes} className="w-3 h-3 sm:w-4 sm:h-4" />
              </button>
            </div>
          ))}
          <input
            type="names"
            id="names"
            name="names"
            placeholder="Enter a new name on each line"
            className="flex-1 min-w-[150px] sm:min-w-[200px] outline-none text-sm sm:text-base"
            value={currentName}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            onBlur={handleBlur}
          />
        </div>
      </div>

      {limitationError && (
        <div className="text-red-500 text-sm mb-2">You can only add up to {limitClassroomStudents} students.</div>
      )}

      {similarityError && <p className="text-storyred text-sm mb-2">Names must be unique. Please remove duplicates.</p>}

      <div
        className={`flex flex-row gap-4 w-full ${isOnboarding ? 'justify-between sm:gap-2' : 'justify-end sm:gap-2'}`}
      >
        {isOnboarding && <BackButton onClick={back}></BackButton>}
        {!isOnboarding && (
          <button
            onClick={close}
            className="bg-transparent hover:bg-[#000000] hover:bg-opacity-20 text-black font-semibold py-2 px-4 rounded-full focus:outline-none focus:ring-1 w-auto flex items-center justify-center text-sm sm:text-base"
          >
            Cancel
          </button>
        )}
        <div className="flex flex-row items-center gap-2 sm:gap-0">
          {isOnboarding && (
            <button
              onClick={skip}
              className="bg-transparent hover:underline text-black font-semibold py-2 rounded-full focus:outline-none w-full sm:w-auto flex items-center justify-center relative sm:mr-4 text-sm sm:text-base"
            >
              I'll do this later
            </button>
          )}

          <button
            className={`px-6 py-2 h-9 bg-secondaryStoryAdditionalLightBlueText text-whiteText font-bold leading-none rounded-2xl hover:shadow-md transition-shadow flex items-center justify-center text-sm sm:text-base w-full sm:w-auto ${
              namesList?.length ? '' : 'opacity-50 cursor-not-allowed'
            }`}
            onClick={() => submit(namesList)}
            disabled={!namesList?.length || loader || limitationError || similarityError}
          >
            Create
            <Loader loader={loader} dimentions="w-6 h-6" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default StudentsAddNewItems;
